import {
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  Grid,
  Typography,
  makeStyles,
} from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import { FilterList } from "@material-ui/icons";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import TreeItem from "@mui/lab/TreeItem";
import TreeView from "@mui/lab/TreeView";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { withTranslation } from "react-i18next";
import { AiOutlineDelete, AiOutlineDownload } from "react-icons/ai";
import { FiList } from "react-icons/fi";
import { MdDashboardCustomize } from "react-icons/md";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { useAuth } from "../../../context/auth";
import { actionShowSpinner } from "../../../redux-flow/action/Wrapper/Spinner";
import ModalBase from "../../Pages/utils/ModalBase";
import {
  deletePhisicallySlot,
  downloadFile,
  listFileById,
  listFiles,
  listFilesByMeetId,
  listFilesBySlotCommitteeId,
  listFolders,
  searchFiles,
} from "../../../services/fileManagement.service";
import DialogCustom from "../../Common/DialogCustom";
import ToastMessage from "../../Common/ToastMessage";
import { checkAccess } from "../utils/index";
import FilterDrawer from "./FilterDrawer";
import FileReader from "../../Common/FileReader";
import FileItem from "./components/FileItem";

import { useFileList } from "../../../context/lists/fileList";
import {
  ActionButton,
  ActionsContainer,
  FileHeaderSection,
  FilesContainer,
  FilesSide,
  FilterButtonsContainer,
  FolderTreeSide,
  SideTitle,
  StyledPaper,
  ViewOptionButton,
  ViewOptionsContainer,
} from "./styles";
import UnsuportedFileModal from "./modals/UnsuportedFIle";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    height: "calc(100% - 100px)",
  },
  subtitle: {
    ...theme.typography.subtitle1,
  },
  button: {
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  buttonOutlined: {
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  filterButton: {
    ...theme.typography.subtitle1,
    backgroundColor: "#fff",
    color: "#666666",
    borderRadius: 8,
    border: "1px solid #666666",
    textTransform: "uppercase",
    maxWidth: 140,
    width: "100%",
  },
  spacer: {
    marginBottom: 20,
  },
  topContainer: {
    marginBottom: 20,
  },
  success: {
    backgroundColor: theme.palette.success.dark,
    borderRadius: 5,
    height: 50,
  },
  label: {
    ...theme.typography.subtitle1,
    color: theme.palette.primary.contrastText,
  },
  icon: {
    color: theme.palette.primary.contrastText,
    fontSize: 30,
    marginRight: 50,
  },
  titleModal: {
    color: "#031795",
    fontWeight: "bold",
    fontSize: "20px",
  },
}));

function FileManagement({ t, actionShowSpinner, history }) {
  const classes = useStyles();
  const firstTimeExecution = useRef(true);
  const { userAccess } = useAuth();
  const { listOptions, updateFilterParams, updateSelectedFolder } =
    useFileList();

  const [viewOption, setViewOption] = useState("list");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [files, setFiles] = useState([]);
  const [
    deletePhisicallyFileDialogOpened,
    setDeletePhisicallyFileDialogOpened,
  ] = useState(false);
  const [filterDrawerOpened, setFilterDrawerOpened] = useState(false);
  const [folders, setFolders] = useState([]);
  const [showDeletedFiles, setShowDeletedFiles] = useState(false);

  const [canDownloadFiles, setCanDownloadFiles] = useState(false);
  const [canSearchFiles, setCanSearchFiles] = useState(false);
  const [canDeleteFiles, setCanDeleteFiles] = useState(false);
  const [canListDeleteFiles, setCanListDeleteFiles] = useState(false);
  const [showUnsuportedFileModal, setShowUnsuportedFileModal] = useState(false);
  const [unsuportedFileBlobUrl, setUnsuportedFileBlobUrl] = useState("");
  const [fileType, setFileType] = useState("");
  const [fileName, setFileName] = useState("");

  const [openedFile, setOpenedFile] = useState(null);

  const [toastMessage, setToastMessage] = useState({
    message: "",
    type: "error",
    open: false,
  });

  useEffect(() => {
    const params = history.location.search;

    const [paramName, paramValue] = params.split("=");

    if (userAccess) {
      const hasAccess = checkAccess(userAccess, "listFiles");
      const hasAccessToDownloadFile = checkAccess(userAccess, "downloadFile");
      const hasAccessToSearchFiles = checkAccess(userAccess, "searchFiles");
      const hasAccessToDeleteFiles = checkAccess(
        userAccess,
        "deleteFilePhisically"
      );
      const hasAccessToListDeleteFiles = checkAccess(
        userAccess,
        "listDeletedFiles"
      );

      setCanDownloadFiles(hasAccessToDownloadFile);
      setCanSearchFiles(hasAccessToSearchFiles);
      setCanDeleteFiles(hasAccessToDeleteFiles);
      setCanListDeleteFiles(hasAccessToListDeleteFiles);

      if (!hasAccess) {
        history.push("/");
      }

      actionShowSpinner(true);

      listFolders(firstTimeExecution && 3)
        .then((response) => {
          const sourceFolder = response.data.find(
            (folder) => folder.name === "sistema_gestao_arquivos"
          );

          setFolders(sourceFolder.childrens);
        })
        .finally(() => {
          firstTimeExecution.current = false;

          if (paramName === "?fileId" && paramValue) {
            getFilesById(paramValue);
          }

          if (paramName === "?meetId" && paramValue) {
            getFilesByMeetId(paramValue);
          }

          if (paramName === "?slotCommitteeId" && paramValue) {
            getFilesBySlotCommitteeId(paramValue);
          }

          actionShowSpinner(false);
        });
    }
  }, [userAccess, history, checkAccess]);

  const getFilesById = useCallback((id) => {
    actionShowSpinner(true);

    listFileById(id)
      .then(({ data: fileResponse }) => {
        const file = fileResponse;

        setFiles([{ ...file, id: file.supportMaterialId }]);
      })
      .finally(() => {
        actionShowSpinner(false);
      });
  }, []);

  const getFilesByMeetId = useCallback((id) => {
    actionShowSpinner(true);

    listFilesByMeetId(id)
      .then(({ data: filesResponse }) => {
        const uniqueFiles = filesResponse.reduce(
          (accumulator, currentValue) => {
            const found = accumulator.some(
              (item) =>
                item.supportMaterialId === currentValue.supportMaterialId
            );

            if (!found) {
              accumulator.push(currentValue);
            }

            return accumulator;
          },
          []
        );

        const formattedFiles = uniqueFiles.map((file) => ({
          ...file,
          id: file.supportMaterialId,
          extension: String(file.extension).toLowerCase(),
        }));

        setFiles(formattedFiles);
      })
      .finally(() => {
        actionShowSpinner(false);
      });
  }, []);

  const getFilesBySlotCommitteeId = useCallback((id) => {
    actionShowSpinner(true);

    listFilesBySlotCommitteeId(id)
      .then(({ data: filesResponse }) => {
        const uniqueFiles = filesResponse.reduce(
          (accumulator, currentValue) => {
            const found = accumulator.some(
              (item) =>
                item.supportMaterialId === currentValue.supportMaterialId
            );

            if (!found) {
              accumulator.push(currentValue);
            }

            return accumulator;
          },
          []
        );

        const formattedFiles = uniqueFiles.map((file) => ({
          ...file,
          id: file.supportMaterialId,
          extension: String(file.extension).toLowerCase(),
        }));

        setFiles(formattedFiles);
      })
      .finally(() => {
        actionShowSpinner(false);
      });
  }, []);

  const getFilesByPath = useCallback(
    (paths, showDeletedFlag) => {
      const showDeleted =
        showDeletedFlag === undefined ? showDeletedFiles : showDeletedFlag;
      actionShowSpinner(true);

      listFiles({
        paths,
        showDeleted,
      })
        .then(({ data: filesResponse }) => {
          const uniqueFiles = filesResponse.reduce(
            (accumulator, currentValue) => {
              const found = accumulator.some(
                (item) =>
                  item.supportMaterialId === currentValue.supportMaterialId
              );

              if (!found) {
                accumulator.push(currentValue);
              }

              return accumulator;
            },
            []
          );

          const formattedFiles = uniqueFiles.map((file) => ({
            ...file,
            id: file.supportMaterialId,
            extension: String(file.extension).toLowerCase(),
          }));

          setFiles(formattedFiles);
        })
        .finally(() => {
          actionShowSpinner(false);
        });
    },
    [showDeletedFiles]
  );

  const filterFiles = useCallback(
    (params) => {
      actionShowSpinner(true);

      searchFiles({
        ...(params.companies && {
          companyIds: params.companies.map((c) => c.id),
        }),
        ...(params.selectedPresenter && {
          presenter: params.selectedPresenter.id,
        }),
        ...(params.selectedRequester && {
          requesterId: params.selectedRequester.id,
        }),
        ...(params.directory && { directorship: params.directory.id }),
        ...(params.fileType && { fileType: params.fileType.id }),
        ...(params.keyword && { keyword: params.keyword }),
        ...(params.committee && { committee: params.committee.id }),
        ...(params.startDate
          ? { startDate: params.startDate }
          : { startDate: null }),
        ...(params.endDate ? { endDate: params.endDate } : { endDate: null }),
        showDeleted: showDeletedFiles,
      })
        .then(({ data: filesResponse }) => {
          const uniqueFiles = filesResponse.reduce(
            (accumulator, currentValue) => {
              const found = accumulator.some(
                (item) =>
                  item.supportMaterialId === currentValue.supportMaterialId
              );

              if (!found) {
                accumulator.push(currentValue);
              }

              return accumulator;
            },
            []
          );

          const formattedFiles = uniqueFiles.map((file) => ({
            ...file,
            id: file.supportMaterialId,
            extension: String(file.extension).toLowerCase(),
          }));

          setFiles(formattedFiles);
        })
        .finally(() => {
          actionShowSpinner(false);
        });

      updateSelectedFolder({ formatted: "", searchPaths: "" });
    },
    [showDeletedFiles]
  );

  const [fetchedFolders, setFetchedFolders] = useState([]);

  const onSelectFolder = useCallback(
    (treeItemDatatreeItemData) => {
      updateSelectedFolder({
        formatted: treeItemDatatreeItemData.displayPath,
        searchPaths: treeItemDatatreeItemData.paths,
        isCommitteeFolder: treeItemDatatreeItemData.isCommitteeFolder,
      });

      const thisFolderHasAlreadyBeenFetched = fetchedFolders.some(
        (folder) => folder === treeItemDatatreeItemData.displayPath
      );

      if (!treeItemDatatreeItemData.isCommitteeFolder) {
        setSelectedFiles([]);

        getFilesByPath(treeItemDatatreeItemData.paths);
      } else if (
        treeItemDatatreeItemData.paths.length > 0 &&
        !thisFolderHasAlreadyBeenFetched
      ) {
        actionShowSpinner(true);
        setFetchedFolders((oldValue) => [
          ...oldValue,
          treeItemDatatreeItemData.displayPath,
        ]);

        listFolders(1, treeItemDatatreeItemData.paths[0]).then((response) => {
          const committeChildrenFolders = response.data;
          let newFolders = folders;

          const newFolderChldrens = folders[0].childrens.map((folder) => {
            if (
              String(folder.name).replaceAll("/", "") ===
              String(treeItemDatatreeItemData.displayPath).replaceAll("/", "")
            ) {
              return committeChildrenFolders[0];
            }

            return folder;
          });

          newFolders[0].childrens = newFolderChldrens;

          setFolders([]);
          setFolders(newFolders);

          setSelectedFiles([]);

          actionShowSpinner(false);
        });
      }
    },
    [folders, fetchedFolders]
  );

  const getTreeItemsFromData = (treeItems) => {
    return treeItems.map((treeItemData) => {
      let children = undefined;

      if (treeItemData.childrens && treeItemData.childrens.length > 0) {
        children = getTreeItemsFromData(treeItemData.childrens);
      }

      return (
        <TreeItem
          onClick={() => onSelectFolder(treeItemData)}
          key={treeItemData.displayPath}
          nodeId={treeItemData.displayPath}
          expande
          label={treeItemData.name}
          children={children}
          style={{ marginTop: "8px" }}
        />
      );
    });
  };

  const formattedTreeItems = useMemo(() => {
    return getTreeItemsFromData(folders);
  }, [folders]);

  const handleFileClick = (fileId) => {
    const isFileSelected = !!selectedFiles.find((file) => file === fileId);

    if (isFileSelected) {
      setSelectedFiles(selectedFiles.filter((file) => file !== fileId));
    } else {
      setSelectedFiles([...selectedFiles, fileId]);
    }
  };

  const handleDownloadFile = async () => {
    actionShowSpinner(true);

    for (const selectedFile of selectedFiles) {
      var { name: fileName, path } = files.find(
        (file) => file.id === selectedFile
      );

      if (!fileName) {
        actionShowSpinner(false);
        return;
      }

      downloadFile(selectedFile, fileName)
        .then((response) => {
          if (response.data) {
          }
        })
        .finally(() => {
          actionShowSpinner(false);
        });
    }
  };

  const handleDeleteFile = async () => {
    actionShowSpinner(true);

    var promises = [];

    for (const selectedFile of selectedFiles) {
      const deletePromise = deletePhisicallySlot(selectedFile).then(
        (response) => {
          if (response.error.length > 0) {
            setToastMessage({
              message: response.error,
              type: "error",
              open: true,
            });
          }
        }
      );

      promises.push(deletePromise);
    }

    Promise.all(promises).finally(() => {
      getFilesByPath(listOptions.selectedFolder.searchPaths);
      actionShowSpinner(false);
      setDeletePhisicallyFileDialogOpened(false);
    });

    setSelectedFiles([]);
  };

  const handleOpenFile = (fileId) => {
    setOpenedFile(fileId);
  };

  const handleOpenUnsupportedFile = (blobUrl, fileName, fileType) => {
    setOpenedFile(null);

    setUnsuportedFileBlobUrl(blobUrl);
    setFileType(fileType);
    setFileName(fileName);
    setShowUnsuportedFileModal(true);
  };

  return (
    <>
      {openedFile && (
        <FileReader
          fileId={openedFile}
          close={() => setOpenedFile(null)}
          actionShowSpinner={actionShowSpinner}
          handleOpenUnsupportedFile={handleOpenUnsupportedFile}
        />
      )}

      <ModalBase
        open={showUnsuportedFileModal}
        setOpen={setShowUnsuportedFileModal}
        content={
          <UnsuportedFileModal
            setModalStatus={setShowUnsuportedFileModal}
            fileName={fileName}
            fileType={fileType}
            blobUrl={unsuportedFileBlobUrl}
          />
        }
      ></ModalBase>

      <Grid container className={classes.root}>
        <ToastMessage
          message={toastMessage.message}
          type={toastMessage.type || "error"}
          open={toastMessage.open}
          close={() => setToastMessage({ ...toastMessage, open: false })}
        />

        <Drawer
          anchor="right"
          open={filterDrawerOpened}
          onClose={() => {
            setFilterDrawerOpened(false);
          }}
        >
          <FilterDrawer
            actionShowSpinner={(opened) => actionShowSpinner(opened)}
            onClose={() => {
              setFilterDrawerOpened(false);
            }}
            t={t}
            setFilters={(newValues) => {
              updateFilterParams(newValues);
              filterFiles(newValues);
            }}
            cleanFilters={(newValues) => {
              updateFilterParams({
                companies: undefined,
                responsible: undefined,
                requesterId: undefined,
                filteType: undefined,
                committeeId: undefined,
                committee: undefined,
                startDate: null,
                endDate: null,
                keyword: undefined,
              });

              if (listOptions.selectedFolder.searchPaths.length > 0) {
                getFilesByPath(listOptions.selectedFolder.searchPaths);
              } else {
                setFiles([]);
              }
            }}
            canListDeleteFiles={canListDeleteFiles}
            filters={listOptions.filterParams}
          />
        </Drawer>

        <DialogCustom
          open={deletePhisicallyFileDialogOpened}
          handleClose={() => {
            setDeletePhisicallyFileDialogOpened(false);
          }}
          handleChange={handleDeleteFile}
          title={t(`forms.dialog.alert`)}
          body={t("forms.dialog.textDeletePhisicallyFile")}
          confirmText={t(`forms.dialog.yes`)}
          cancelText={t(`forms.dialog.no`)}
          alert={true}
        />

        <Grid item className={classes.spacer}>
          <Typography variant="h4">
            {t("menu.fileManagement.info.title")}
          </Typography>
        </Grid>

        <Grid container className={classes.spacer}>
          <FilterButtonsContainer>
            <Button
              className={classes.filterButton}
              startIcon={<FilterList style={{ fontSize: 30 }} />}
              onClick={() => setFilterDrawerOpened(true)}
              disabled={!canSearchFiles}
            >
              {t("forms.filters")}
            </Button>

            {canListDeleteFiles && (
              <FormControlLabel
                fullWidth
                control={
                  <Checkbox
                    value={showDeletedFiles}
                    checked={showDeletedFiles}
                    onChange={() => {
                      if (
                        listOptions?.selectedFolder?.searchPaths?.length > 0
                      ) {
                        getFilesByPath(
                          listOptions.selectedFolder.searchPaths,
                          !showDeletedFiles
                        );
                      }

                      setShowDeletedFiles(!showDeletedFiles);
                    }}
                    color="primary"
                  />
                }
                label={t("menu.fileManagement.info.showDeletedFiles")}
              />
            )}
          </FilterButtonsContainer>

          <ActionsContainer>
            <Tooltip aria-label="download" title={t("forms.dialog.download")}>
              <ActionButton
                disabled={selectedFiles.length < 1 || !canDownloadFiles}
                onClick={handleDownloadFile}
              >
                <AiOutlineDownload size={26} />
              </ActionButton>
            </Tooltip>

            <Tooltip aria-label="delete" title={t("forms.dialog.delete")}>
              <ActionButton
                disabled={selectedFiles.length < 1 || !canDeleteFiles}
                onClick={() => setDeletePhisicallyFileDialogOpened(true)}
              >
                <AiOutlineDelete size={26} />
              </ActionButton>
            </Tooltip>
          </ActionsContainer>
        </Grid>

        <StyledPaper>
          <FolderTreeSide>
            <SideTitle>{t("menu.fileManagement.info.folders")}</SideTitle>
            <TreeView
              aria-label="file system navigator"
              defaultCollapseIcon={<ExpandMoreIcon />}
              defaultExpandIcon={<ChevronRightIcon />}
              sx={{ flexGrow: 1, maxWidth: 400, overflowY: "auto" }}
              selected={listOptions.selectedFolder.formatted}
            >
              {formattedTreeItems}
            </TreeView>
          </FolderTreeSide>
          <FilesSide>
            <FileHeaderSection>
              <SideTitle>{t("menu.fileManagement.info.files")}</SideTitle>

              <ViewOptionsContainer>
                <Tooltip
                  aria-label="fileViewListOption"
                  title={t("fileViewListOption")}
                >
                  <ViewOptionButton
                    selected={viewOption === "list"}
                    onClick={() => setViewOption("list")}
                  >
                    <FiList />
                  </ViewOptionButton>
                </Tooltip>

                <Tooltip
                  aria-label="fileViewIconsOption"
                  title={t("fileViewIconsOption")}
                >
                  <ViewOptionButton
                    selected={viewOption === "icons"}
                    onClick={() => setViewOption("icons")}
                  >
                    <MdDashboardCustomize />
                  </ViewOptionButton>
                </Tooltip>
              </ViewOptionsContainer>
            </FileHeaderSection>

            <FilesContainer displayType={viewOption}>
              {files.map((file) => (
                <FileItem
                  displayType={viewOption}
                  t={t}
                  key={file.id}
                  selected={
                    !!selectedFiles.find(
                      (selectedFile) => file.id === selectedFile
                    )
                  }
                  fileInfo={file}
                  onFileClick={() => handleFileClick(file.id)}
                  handleOpenFile={handleOpenFile}
                />
              ))}
            </FilesContainer>
          </FilesSide>
        </StyledPaper>
      </Grid>
    </>
  );
}

const withRouterFileManagement = withRouter(FileManagement);

const mapDispatchToProps = (dispatch) => ({
  actionShowSpinner: (request) => dispatch(actionShowSpinner(request)),
});

const FileManagementConnect = connect(
  null,
  mapDispatchToProps
)(withRouterFileManagement);

export default withTranslation()(FileManagementConnect);
